import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
  reportConciliationHeaderMetaSelector,
  changeConciliationHeaderFilterByStateFilterAction,
  reportConciliationHeaderFilterByStateSelector,
} from '../state';
import { getStatusColor, getStatusBackground } from '../helpers';
import InfoCard from './InfoCard';

import {
  concHeaderStateType,
  ConcHeaderStateType,
  ConcHeaderStateCount,
  concHeaderStateValueToKey,
} from '../types';

const ORDER = [
  concHeaderStateType.CONCILIATION_ERROR,
  concHeaderStateType.CONCILIATION_OK,
] as const satisfies readonly ConcHeaderStateType[];

const iconByCode: Partial<Record<ConcHeaderStateType, typeof HighlightOffIcon>> = {
  [concHeaderStateType.CONCILIATION_ERROR]: HighlightOffIcon,
  [concHeaderStateType.CONCILIATION_OK]: CheckCircleOutlineIcon,
};

const ConciliationHeaderInfoCards = () => {
  const meta = useSelector(reportConciliationHeaderMetaSelector);
  const stateCounts = (meta?.stateCounts ?? {}) as Partial<ConcHeaderStateCount>;

  const getCount = (code: ConcHeaderStateType): number => {
    const key = concHeaderStateValueToKey[code];
    return (stateCounts?.[key] ?? 0) as number;
  };

  const total = useMemo(() => ORDER.reduce((acc, code) => acc + getCount(code), 0), [stateCounts]);

  const items = useMemo(
    () =>
      ORDER.map(errorCode => ({
        errorCode,
        value: getCount(errorCode),
        Icon: iconByCode[errorCode],
      })),
    [stateCounts]
  );

  const hasAny = items.some(({ value }) => value > 0);
  if (!hasAny) return null;

  const commonProps = {
    total,
    getColor: getStatusColor,
    getBackground: getStatusBackground,
    translationKey: 'conciliation-header.state-types-labels' as const,
    unitLabel: 'Restaurants',
    dispatchAction: changeConciliationHeaderFilterByStateFilterAction,
    selectedFilterSelector: reportConciliationHeaderFilterByStateSelector,
  };

  return stateCounts && Object.values(stateCounts).some(value => Boolean(value)) ? (
    <Box display="flex" gap={4} sx={{ mt: { xs: 1, md: 1, lg: 2, xl: 2 } }}>
      <InfoCard
        {...commonProps}
        value={stateCounts.CONCILIATION_ERROR || 0}
        errorCode={concHeaderStateType.CONCILIATION_ERROR}
        Icon={HighlightOffIcon}
      />
      <InfoCard
        {...commonProps}
        value={stateCounts.CONCILIATION_OK || 0}
        errorCode={concHeaderStateType.CONCILIATION_OK}
        Icon={CheckCircleOutlineIcon}
      />
    </Box>
  ) : null;
};

export default ConciliationHeaderInfoCards;
